import {DTO_STATE} from '@mybake/ui/common/dictionary';
import {IDecoration} from '../../models/Decorations/IDecoration';
import {IOrderDecoration} from '../../models/Order/IOrderDecoration';
import {IProductType} from '../../models/ProductTypes/IProductType';
import {IRecipe} from '../../models/Recipe/IRecipe';
import {roundMoney} from './subOrderCost';

export type RecommendedPriceResult = {
    isComplete: boolean;
    unitPrice?: number;
    linePrice?: number;
    missing: string[];
};

const active = <T extends {state: unknown}>(value: T) => value.state !== DTO_STATE.DTO_DELETED;

export const calculateDecorationRecommendedPrice = (decoration: IDecoration, selectedQuantity: number): number | undefined => {
    if (!Number.isInteger(selectedQuantity) || selectedQuantity <= 0) return undefined;
    const options = (decoration.priceOptions ?? [])
        .filter(active)
        .filter(option => Number.isInteger(Number(option.quantity)) && Number(option.quantity) > 0 && Number(option.price) >= 0)
        .sort((a, b) => Number(b.quantity) - Number(a.quantity));
    let remaining = selectedQuantity;
    let total = 0;
    while (remaining > 0) {
        const option = options.find(candidate => Number(candidate.quantity) <= remaining);
        if (!option) return undefined;
        remaining -= Number(option.quantity);
        total += Number(option.price);
    }
    return roundMoney(total);
};

type Params = {
    recipe?: IRecipe;
    selectedProportion: number;
    count: number;
    selectedDecorations?: IOrderDecoration[];
    productTypes?: IProductType[];
    decorations?: IDecoration[];
};

export const calculateRecommendedPrice = ({recipe, selectedProportion, count, selectedDecorations = [], productTypes = [], decorations = []}: Params): RecommendedPriceResult => {
    const missing: string[] = [];
    const productType = productTypes.find(type => type.id === recipe?.productTypeId);
    const tier = (productType?.priceTiers ?? [])
        .filter(active)
        .filter(candidate => Number(candidate.fromValue) <= Number(selectedProportion))
        .sort((a, b) => Number(b.fromValue) - Number(a.fromValue))[0];
    if (!recipe?.productTypeId || !productType || !tier) missing.push(productType?.name || recipe?.productTypeName || 'product-type');

    let decorationsPrice = 0;
    selectedDecorations.filter(active).forEach(selected => {
        const decoration = decorations.find(candidate => candidate.id === selected.decorationId);
        const price = decoration ? calculateDecorationRecommendedPrice(decoration, Number(selected.quantity)) : undefined;
        if (price === undefined) missing.push(selected.name || decoration?.name || 'decoration');
        else decorationsPrice += price;
    });

    const recipePrice = tier ? Number(tier.price) * Number(selectedProportion) : 0;
    const unitPrice = roundMoney(recipePrice + decorationsPrice);
    return {isComplete: missing.length === 0, unitPrice, linePrice: roundMoney(unitPrice * Number(count)), missing};
};
